import React from 'react';
import {
  Box,
  Heading,
  FormControl,
  FormLabel,
  Input,
  Button,
  Switch,
  Textarea,
  Flex,
  Divider,
  useToast,
  Spinner,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Text,
  Badge,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Stat,
  StatLabel,
  StatNumber,
  StatGroup,
} from '@chakra-ui/react';
import { useParams, useNavigate } from 'react-router-dom';
import { FiSave, FiArrowLeft } from 'react-icons/fi';
import xhr from 'xhr';
import ProgramSelector from './components/ProgramSelector';
import SchoolUsers from './components/SchoolUsers';
import EnrollStudent from './components/EnrollStudent';

export default function SchoolDetail() {
  const { id: paramId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();

  const [resolvedId, setResolvedId] = React.useState(paramId === 'mi-school' ? null : paramId);
  const isNew = paramId === 'nueva';
  const id = resolvedId;

  const [loading, setLoading] = React.useState(!isNew);
  const [saving, setSaving] = React.useState(false);
  const [school, setSchool] = React.useState({
    name: '',
    domain: '',
    contact_name: '',
    contact_email: '',
    cellphone: '',
    whatsapp_students: '',
    country: '',
    city: '',
    logo_url: '',
    active: true,
    notes: '',
  });
  const [stats, setStats] = React.useState({ students: 0, payments: 0 });

  // Resolve "mi-school" to actual school ID from current user
  React.useEffect(() => {
    if (paramId !== 'mi-school') return;
    const resolveMySchool = async () => {
      try {
        const res = await xhr('/users/me?populate=school');
        const schoolData = res.data?.school;
        if (schoolData?.id) {
          setResolvedId(String(schoolData.id));
        } else {
          toast({ title: 'No se encontro tu institucion vinculada', status: 'error', position: 'bottom', duration: 5000, isClosable: true });
        }
      } catch (error) {
        console.error('Error resolving school:', error);
      }
    };
    resolveMySchool();
  }, [paramId]);

  React.useEffect(() => {
    if (isNew || !id) return;

    const fetchSchool = async () => {
      try {
        const res = await xhr(`/schools/${id}?populate=*`);
        const data = res.data.data;
        const attrs = data.attributes || data;
        setSchool({
          name: attrs.name || '',
          domain: attrs.domain || '',
          contact_name: attrs.contact_name || '',
          contact_email: attrs.contact_email || '',
          cellphone: attrs.cellphone || '',
          whatsapp_students: attrs.whatsapp_students || '',
          country: attrs.country || '',
          city: attrs.city || '',
          logo_url: attrs.logo_url || '',
          active: attrs.active !== false,
          notes: attrs.notes || '',
        });

        // Fetch stats
        const [studentsRes, paymentsRes] = await Promise.all([
          xhr(`/students?filters[schoolFrom][id][$eq]=${id}&pagination[pageSize]=1&pagination[withCount]=true`),
          xhr(`/payment-schools?filters[school][id][$eq]=${id}&pagination[pageSize]=1&pagination[withCount]=true`),
        ]);
        setStats({
          students: studentsRes.data.meta?.pagination?.total || 0,
          payments: paymentsRes.data.meta?.pagination?.total || 0,
        });
      } catch (error) {
        console.error('Error fetching school:', error);
        toast({ title: 'Error al cargar la institución', status: 'error', position: 'bottom', duration: 4000, isClosable: true });
      } finally {
        setLoading(false);
      }
    };

    fetchSchool();
  }, [id]);

  const handleChange = (field, value) => {
    setSchool((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!school.name || !school.domain || !school.contact_name || !school.contact_email || !school.logo_url) {
      toast({ title: 'Campos requeridos', description: 'Nombre, Subdominio, Contacto, Email y Logo son obligatorios', status: 'warning', position: 'bottom', duration: 5000, isClosable: true });
      return;
    }

    try {
      setSaving(true);
      const payload = { data: school };

      if (isNew) {
        const res = await xhr.post('/schools', payload);
        const newId = res.data.data.id;
        toast({ title: 'Institución creada exitosamente', status: 'success', position: 'bottom', duration: 3000, isClosable: true });
        navigate(`/plataforma/administrador/instituciones/${newId}`, { replace: true });
      } else {
        await xhr.put(`/schools/${id}`, payload);
        toast({ title: 'Cambios guardados correctamente', status: 'success', position: 'bottom', duration: 3000, isClosable: true });
      }
    } catch (error) {
      const message = error.response?.data?.error?.message || 'Error al guardar';
      toast({ title: 'Error al guardar', description: message, status: 'error', position: 'bottom', duration: 5000, isClosable: true });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box p={8} textAlign="center">
        <Spinner size="lg" />
      </Box>
    );
  }

  return (
    <Box p={4}>
      <Flex alignItems="center" gap={3} mb={6}>
        <Button
          variant="ghost"
          leftIcon={<FiArrowLeft />}
          onClick={() => navigate('/plataforma/administrador/instituciones')}
        >
          Instituciones
        </Button>
        <Heading size="md">{isNew ? 'Nueva Institución' : school.name}</Heading>
        {!isNew && (
          <Badge colorScheme={school.active ? 'green' : 'red'} ml={2}>
            {school.active ? 'Activo' : 'Inactivo'}
          </Badge>
        )}
      </Flex>

      <Tabs colorScheme="purple" isLazy>
        <TabList>
          <Tab>Datos Generales</Tab>
          {!isNew && <Tab>Programas</Tab>}
          {!isNew && <Tab>Estudiantes</Tab>}
          {!isNew && <Tab>Usuarios Admin</Tab>}
          {!isNew && <Tab>Resumen</Tab>}
        </TabList>

        <TabPanels>
          {/* Tab: Datos Generales */}
          <TabPanel>
            <Box bg="white" p={6} borderRadius="lg" boxShadow="sm">
              <Flex gap={4} mb={4} flexWrap="wrap">
                <FormControl flex="1" minW="250px" isRequired>
                  <FormLabel>Nombre de la Institución</FormLabel>
                  <Input
                    value={school.name}
                    onChange={(e) => handleChange('name', e.target.value)}
                    placeholder="Ej: Instituto ABC"
                  />
                </FormControl>
                <FormControl flex="1" minW="250px" isRequired>
                  <FormLabel>Subdominio</FormLabel>
                  <Input
                    value={school.domain}
                    onChange={(e) => handleChange('domain', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                    placeholder="Ej: instituto-abc"
                  />
                  {school.domain && (
                    <Text fontSize="xs" color="gray.500" mt={1}>
                      {school.domain}.eduplataforma.co
                    </Text>
                  )}
                </FormControl>
              </Flex>

              <Flex gap={4} mb={4} flexWrap="wrap">
                <FormControl flex="1" minW="250px" isRequired>
                  <FormLabel>Nombre de Contacto</FormLabel>
                  <Input
                    value={school.contact_name}
                    onChange={(e) => handleChange('contact_name', e.target.value)}
                  />
                </FormControl>
                <FormControl flex="1" minW="250px" isRequired>
                  <FormLabel>Email de Contacto</FormLabel>
                  <Input
                    type="email"
                    value={school.contact_email}
                    onChange={(e) => handleChange('contact_email', e.target.value)}
                  />
                </FormControl>
              </Flex>

              <Flex gap={4} mb={4} flexWrap="wrap">
                <FormControl flex="1" minW="250px">
                  <FormLabel>Teléfono</FormLabel>
                  <Input
                    value={school.cellphone}
                    onChange={(e) => handleChange('cellphone', e.target.value)}
                  />
                </FormControl>
                <FormControl flex="1" minW="250px">
                  <FormLabel>WhatsApp para estudiantes</FormLabel>
                  <Input
                    value={school.whatsapp_students}
                    onChange={(e) => handleChange('whatsapp_students', e.target.value)}
                    placeholder="Ej: 3174492220"
                  />
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    Número que verán los estudiantes en Contáctenos
                  </Text>
                </FormControl>
              </Flex>

              <Flex gap={4} mb={4} flexWrap="wrap">
                <FormControl flex="1" minW="250px">
                  <FormLabel>País</FormLabel>
                  <Input
                    value={school.country}
                    onChange={(e) => handleChange('country', e.target.value)}
                  />
                </FormControl>
                <FormControl flex="1" minW="250px">
                  <FormLabel>Ciudad</FormLabel>
                  <Input
                    value={school.city}
                    onChange={(e) => handleChange('city', e.target.value)}
                  />
                </FormControl>
              </Flex>

              <FormControl mb={4} isRequired>
                <FormLabel>URL del Logo</FormLabel>
                <Input
                  value={school.logo_url}
                  onChange={(e) => handleChange('logo_url', e.target.value)}
                  placeholder="https://..."
                />
              </FormControl>

              <FormControl mb={4}>
                <FormLabel>Notas Internas</FormLabel>
                <Textarea
                  value={school.notes}
                  onChange={(e) => handleChange('notes', e.target.value)}
                  rows={3}
                />
              </FormControl>

              <Flex justifyContent="space-between" alignItems="center">
                <FormControl display="flex" alignItems="center" w="auto">
                  <FormLabel mb={0} mr={3}>Activo</FormLabel>
                  <Switch
                    colorScheme="green"
                    isChecked={school.active}
                    onChange={(e) => handleChange('active', e.target.checked)}
                  />
                </FormControl>

                <Button
                  leftIcon={<FiSave />}
                  colorScheme="purple"
                  onClick={handleSave}
                  isLoading={saving}
                >
                  {isNew ? 'Crear Institución' : 'Guardar Cambios'}
                </Button>
              </Flex>
            </Box>
          </TabPanel>

          {/* Tab: Programas */}
          {!isNew && (
            <TabPanel>
              <ProgramSelector schoolId={id} />
            </TabPanel>
          )}

          {/* Tab: Estudiantes */}
          {!isNew && (
            <TabPanel>
              <EnrollStudent schoolId={id} />
            </TabPanel>
          )}

          {/* Tab: Usuarios Admin */}
          {!isNew && (
            <TabPanel>
              <SchoolUsers schoolId={id} />
            </TabPanel>
          )}

          {/* Tab: Resumen */}
          {!isNew && (
            <TabPanel>
              <Box bg="white" p={6} borderRadius="lg" boxShadow="sm">
                <StatGroup>
                  <Stat>
                    <StatLabel>Estudiantes Registrados</StatLabel>
                    <StatNumber>{stats.students}</StatNumber>
                  </Stat>
                  <Stat>
                    <StatLabel>Pagos Registrados</StatLabel>
                    <StatNumber>{stats.payments}</StatNumber>
                  </Stat>
                </StatGroup>
              </Box>
            </TabPanel>
          )}
        </TabPanels>
      </Tabs>
    </Box>
  );
}
